import { Box, Button, Card, CardContent, FormHelperText, Grid, CardHeader,
  Drawer, useMediaQuery,  Stack, Typography, List, ListItem, Divider,
  Tooltip} from "@mui/material";
import { useFormik } from "formik";
import { FC, useCallback, useState } from "react";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "~/components/formik/OdForm";
import TextField from "~/components/formik/TextField";
import Create from "~/controller/Create";
import { useSimpleSaveAction } from "~/controller/SaveContextProvider";
import { ClientType, ShareSecretResponse } from "~/gen/api/user";
import { autoCopyTextFieldProps } from "~/utils/clipboard";
import ReadonlyField from "~/components/input/ReadonlyField";
import { useDispatch } from "react-redux";
import { useListContext } from "ra-core";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import { crudGetOneEx } from "~/actions/crudFetchEx";
  

const validationSchema =
  Yup.object().shape({
    clientName: Yup.string().max(120).required('Client Name is required'),
    remark: Yup.string().max(512),
    clientType: Yup.number().required('Client Type is required'),
  })

export interface AddShareSecretClientProps {
  open: boolean;
  setOpen: (x:boolean) => void;
}

const AddShareSecretClient: FC<AddShareSecretClientProps> = (props) => {
  const handlers = useDefaultSavedHandlers("Create Client")
  return (
    <Create resource="settings-client-share-secret" basePath="" {...handlers}>
      <AddShareSecretClientBody {...props}/>
    </Create>
  )
}

const AddShareSecretClientBody: FC<AddShareSecretClientProps> = ({
  open,
  setOpen,
}) => {
  const [states, setStates] = useState({
    creationResult: null as ShareSecretResponse|null,
    copied: false,
  })
  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  const save = useSimpleSaveAction()
  const { refetch } = useListContext()
  const initialValues = {
    submit: '',
    clientName: '',
    remark: '',
    clientType: ClientType.ShareSecretDataDriver,
  }

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: ({ data }: {data:ShareSecretResponse}) => {
        setStatus({ success: true })
        setSubmitting(false)
        setStates(v => ({...v, creationResult: data }))
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    save({...values }, options)      
  }, [save])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, isSubmitting, values, resetForm } = formikCtrl
  
  const dispatch = useDispatch()
  const handleClose = (e:any, reason?:string): void => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') { return }
    setOpen(false);
    if (states.creationResult) {
      dispatch(crudGetOneEx('settings-client-share-secret', states.creationResult.clientId || '', ''))
      setStates(v => ({...v, creationResult: null}))
      refetch()
    }
    resetForm({ values: initialValues, touched: {} })
  };


  return (
    <Drawer
      anchor="right"
      // onOpen={() => {}}
      ModalProps={{
        onClose: handleClose
      }}
      onClose={() => {}}
      open={open}
      PaperProps={{ sx: { p: 2, width: width, } }}
    >
      <Card sx={{ overflowY: 'auto', mb: 4, }} >
        <CardHeader title="Create SharedSecret Client" />
        <CardContent>
          <OdForm formikCtrl={formikCtrl}>

            <Grid container spacing={4} >
              {!states.creationResult ?
                <>
                  <Grid item xs={12} >
                    <TextField label="Client Name*" name="clientName" />
                  </Grid>
                  {/* <Grid item xs={12} >
                    <SelectField label="Usage*" name="clientType" >
                      <MenuItem value={ClientType.ShareSecretDataDriver}>as Data Driver</MenuItem>
                      <MenuItem value={ClientType.ShareSecretEncryptionTool}>as File Encryptor</MenuItem>
                    </SelectField>
                  </Grid> */}
                  <Grid item xs={12} >
                    <TextField label="Remark" name="remark" multiline={true} rows={4} />
                  </Grid>
                </> :
                <>
                  <Grid item xs={12} >
                    <List>
                      <ListItem>
                        <Typography variant="h6" >{values.clientName}</Typography>
                      </ListItem>
                      {values.remark &&
                        <>
                          <Divider />
                          <ListItem>
                            <Typography variant="body2">
                              <Typography variant="body1" component="span">Remark: </Typography>
                              {values.remark}
                            </Typography>
                          </ListItem>
                        </>
                      }
                    </List>
                  </Grid>
                  <Grid item xs={12}>
                    <Tooltip title={states.copied ? 'Copied' : 'Click to Copy'}>
                      <Box>
                        <ReadonlyField 
                          id="client-id-generated"
                          label="Client Id"
                          value={states.creationResult.clientId || ''}
                          {...autoCopyTextFieldProps(
                            () => setStates(v => ({...v, copied: true})), 
                            () => setTimeout(() => setStates(v => ({...v, copied: false})), 500)
                          )}
                          {...{inputProps: {tabIndex: 0}}}
                        />
                      </Box>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12}>
                    <Tooltip title={states.copied ? 'Copied' : 'Click to Copy'}>
                      <Box>
                        <ReadonlyField 
                          id="client-secret-generated"
                          label="Client Secret"
                          value={states.creationResult.clientSecret || ''}
                          helperText="Please copy this ClientSecret and save it to somewhere safe. OnData will only display it for once."
                          {...autoCopyTextFieldProps(
                            () => setStates(v => ({...v, copied: true})), 
                            () => setTimeout(() => setStates(v => ({...v, copied: false})), 500)
                          )}
                          {...{inputProps: {tabIndex: 0}}}
                        />
                      </Box>
                    </Tooltip>
                  </Grid>
                </>
              }
            </Grid>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}
            <Stack 
              direction="row"
              justifyContent="space-between"
              sx={{ py: 2 }} 
            >
              <Button
                color="primary"
                disabled={isSubmitting}
                variant="outlined"
                onClick={handleClose}
              >
                Cancel
              </Button>
              {!states.creationResult &&
                <Button
                  color="primary"
                  disabled={isSubmitting}
                  type="submit"
                  variant="contained"
                >
                  Save
                </Button>
              }
            </Stack>

          </OdForm>
        </CardContent>
      </Card>
    </Drawer>
  )
}


export default AddShareSecretClient;
